import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import LayeredMultiAutocompleteSelect from "../components/LayeredMultiAutocompleteSelect";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { integrationApiInstance } from "../apis/integrationApi";
import AgentAuthorizationsStep from "../features/authorizations/AgentAuthorizationsStep";
import { AuthorizationPolicy, DEFAULT_AUTHORIZATION_POLICY } from "../features/authorizations/authorizationPolicy";
import {
  AuthorizationSettingsStore,
  agentPolicyFromWire,
  horizonAuthorizationSettingsStore,
  policyToWire,
} from "../features/authorizations/authorizationSettingsStore";
import { DirectorySource } from "../features/authorizations/customerApprovers";
import { useCustomerApprovers } from "../features/authorizations/useCustomerApprovers";
import {
  useAgentFilterOptions,
  type AgentFilterQueueOption,
  type AgentFilterTaxonomyOption,
} from "../hooks/useAgentFilterOptions";
import { useAgents } from "../hooks/useAgents";
import { useIntegration } from "../hooks/useIntegration";
import { useUserMsp } from "../hooks/useUserMsp";
import { NEUTRAL, RADIUS, VIOLET } from "../styles/brandTokens";
import { AutoResolverSummary } from "./agentCapacity";
import {
  M365SensitiveWritesControl,
  defaultM365SensitiveWriteModes,
  normalizeM365SensitiveWriteModes,
  type M365SensitiveWriteModes,
} from "./m365SensitiveActions";
import { TaxonomyBrowser } from "./taxonomyBrowser";
import {
  TimeEntryMinutesList,
  timeEntryDraftFromAgent,
  timeEntryMinutesForSave,
  timeEntryProblems,
  type TimeEntryDraft,
} from "./agentTimeEntry";
import type {
  Agent,
  AgentInProgressStatus,
  AgentInput,
  AgentNotifyEvent,
  AgentTrigger,
  AgentUpdateField,
  AutomationCapacity,
  AutomationTriggerEvent,
  Customer,
  CustomerInteraction,
} from "../types/integration";
import {
  AGENT_NOTIFY_EVENTS,
  AGENT_UPDATE_FIELDS,
  TIME_ENTRY_MAX_MINUTES,
  TIME_ENTRY_MIN_MINUTES,
  notifyTargetsFromNotify,
} from "../types/integration";
import {
  AUTOMATION_TRIGGER_EVENTS,
  normalizeAgentUpdateFields,
  normalizeAgentTriggerEvents,
} from "../types/integration";

/**
 * Configure an agent: the main setup, not nested anywhere.
 *
 * 1. Name — the agent's name and description.
 * 2. Filters and triggers — which tickets match, and whether creation or update fires it.
 * 3. Instructions — operating notes added to this agent's turns. The box is locked.
 * 4. Authorizations — who may authorize this agent starting work on a ticket. The policy is this
 *    agent's own; the customer approvers it names are shared by every agent.
 * 5. Autonomy — Auto Resolver, which sensitive Microsoft 365 writes wait for a technician, and the
 *    PSA status set when Theo starts a ticket.
 * 6. Communicate — which notifications it sends, and where those messages go.
 * 7. Time Entry — minutes Theo logs when an Auto Resolver run completes, per Theo L1 ticket category.
 *    Each field shows the shared default as its placeholder; only typed values are saved as overrides.
 *
 * Every agent uses this screen, including the built-in Theo Technician Agent at
 * /theo-automations/configure. The poller starts that agent from the trigger, autonomy,
 * and notification settings saved here. The open step is `?tab=` (a slug such as
 * `autonomy`, or the step number shown on the tab) so a link can open that step.
 */

const STEP_TABS = ["Name", "Trigger & Filters", "Instructions", "Authorizations", "Autonomy", "Communicate", "Time Entry"] as const;
const STEP_TAB_PARAMS = ["name", "trigger", "instructions", "authorizations", "autonomy", "communicate", "time-entry"] as const;

function stepFromTabParam(value: string | null): number | null {
  const raw = (value || "").trim().toLowerCase().replace(/[\s_]+/g, "-");
  if (!raw) return null;
  const named = STEP_TAB_PARAMS.indexOf(raw as (typeof STEP_TAB_PARAMS)[number]);
  if (named >= 0) return named;
  if (/^[1-7]$/.test(raw)) return Number(raw) - 1;
  return null;
}
const AUTHORIZATIONS_STEP = 3;
const AUTONOMY_STEP = 4;
const COMMUNICATE_STEP = 5;
const TIME_ENTRY_STEP = 6;

const NOTIFY_EVENT_COPY: Record<AgentNotifyEvent, { title: string; body: string }> = {
  picked_up: { title: "Picks up a ticket", body: "When this agent starts working a ticket." },
  needs_attention: { title: "Needs a technician", body: "A question, sign-in, or approval is waiting." },
  finished: { title: "Finishes", body: "The ticket is done." },
};

const TRIGGER_EVENT_COPY: Record<AutomationTriggerEvent, { title: string; body: string }> = {
  ticket_created: {
    title: "Ticket creation",
    body: "Newly detected tickets this agent's filters match.",
  },
  ticket_updated: {
    title: "Ticket update",
    body: "Already-tracked tickets whose selected fields change and still match the filters on this step.",
  },
};

const UPDATE_FIELD_COPY: Record<AgentUpdateField, string> = {
  status: "Status",
  priority: "Priority",
  assignee: "Assignee",
  title: "Title",
  description: "Description",
  queue: "Queue or category",
};

function linesToList(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function listToLines(values: string[] | undefined): string {
  return (values ?? []).join("\n");
}

/** One dropdown row per stored id. A saved id the PSA no longer lists stays selectable. */
export function queueFilterOptions(
  options: AgentFilterQueueOption[],
  selectedIds: string[]
): { value: string; label: string }[] {
  const byValue = new Map<string, { value: string; label: string }>();
  for (const option of options) {
    const value = String(option.id);
    const existing = byValue.get(value);
    if (!existing) {
      byValue.set(value, { value, label: option.name });
    } else if (existing.label !== option.name) {
      byValue.set(value, { value, label: `${existing.label} / ${option.name}` });
    }
  }
  for (const id of selectedIds) {
    if (!byValue.has(id)) byValue.set(id, { value: id, label: id });
  }
  return Array.from(byValue.values()).sort((left, right) => left.label.localeCompare(right.label));
}

/** Matches a ticket with nobody assigned. The poller uses the same id. */
export const UNASSIGNED_ASSIGNEE_ID = "unassigned";

/** Dropdown rows for a PSA id filter. A saved id the PSA no longer lists stays selectable. */
export function labeledFilterOptions(
  options: Array<{ id: string; label: string }>,
  selectedIds: string[],
  pinned: Array<{ id: string; label: string }> = []
): { value: string; label: string }[] {
  const byValue = new Map<string, { value: string; label: string }>();
  const pinnedIds = new Set<string>();
  for (const option of pinned) {
    const value = String(option.id || "").trim();
    if (!value) continue;
    pinnedIds.add(value);
    byValue.set(value, { value, label: option.label || value });
  }
  for (const option of options) {
    const value = String(option.id || "").trim();
    if (!value || byValue.has(value)) continue;
    byValue.set(value, { value, label: option.label || value });
  }
  for (const id of selectedIds) {
    if (id && !byValue.has(id)) byValue.set(id, { value: id, label: id });
  }
  const pinnedRows = Array.from(byValue.values()).filter((row) => pinnedIds.has(row.value));
  const rest = Array.from(byValue.values())
    .filter((row) => !pinnedIds.has(row.value))
    .sort((left, right) => left.label.localeCompare(right.label));
  return [...pinnedRows, ...rest];
}

const labelSx = { fontSize: 13, fontWeight: 600, color: NEUTRAL.text, display: "block", mb: 0.75 } as const;
const helpSx = { fontSize: 12.5, lineHeight: 1.5, color: NEUTRAL.textSecondary } as const;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.control}px`,
    backgroundColor: NEUTRAL.surface,
    fontSize: 14,
    "& fieldset": { borderColor: NEUTRAL.border },
    "&:hover fieldset": { borderColor: NEUTRAL.borderStrong },
  },
} as const;

const selectSx = {
  ...fieldSx,
  borderRadius: `${RADIUS.control}px`,
  fontSize: 14,
  backgroundColor: NEUTRAL.surface,
} as const;

const checkboxSx = { color: NEUTRAL.borderStrong, "&.Mui-checked": { color: VIOLET.main } } as const;

const notifySectionSx = {
  p: 2,
  borderRadius: `${RADIUS.card}px`,
  backgroundColor: NEUTRAL.page,
  border: `1px solid ${NEUTRAL.border}`,
} as const;

const Step: React.FC<{
  index: number;
  title: string;
  help: string;
  hidden?: boolean;
  onNext?: () => void;
  children: React.ReactNode;
}> = ({ index, title, help, hidden, onNext, children }) => {
  if (hidden) return null;
  return (
  <Box
    component="section"
    sx={{
      mt: 1.5,
      p: 2.5,
      borderRadius: `${RADIUS.card}px`,
      backgroundColor: NEUTRAL.surface,
      border: `1px solid ${NEUTRAL.border}`,
    }}
  >
    <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>
      Step {index} — {title}
    </Typography>
    <Typography sx={{ ...helpSx, mt: 0.5 }}>{help}</Typography>
    <Box sx={{ mt: 2 }}>{children}</Box>
    {onNext ? (
      <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={onNext}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            borderRadius: `${RADIUS.control}px`,
            borderColor: VIOLET.main,
            color: VIOLET.theoText,
          }}
        >
          Next step
        </Button>
      </Box>
    ) : null}
  </Box>
  );
};

interface AgentDraft {
  name: string;
  description: string;
  enabled: boolean;
  events: AutomationTriggerEvent[];
  updateFields: AgentUpdateField[];
  ticketTypes: string;
  queues: string;
  statuses: string;
  priorities: string;
  assignees: string;
  // Empty means every customer: customers are a filter, not a separate scope.
  customerIds: number[];
  ticketTypeMode: "free" | "taxonomy";
  taxonomy: Array<{ l1: string; l2: string | null }>;
  instructions: string;
  authorization: AuthorizationPolicy;
  capacity: AutomationCapacity;
  interaction: CustomerInteraction | null;
  notifyEvents: AgentNotifyEvent[];
  notifyTechnician: boolean;
  notifyChannel: boolean;
  notifyTeamId: string;
  notifyChannelId: string;
  m365SensitiveWrites: M365SensitiveWriteModes;
  // Empty uses the PSA's own "In Progress" label.
  inProgressStatusId: string;
  // Empty leaves the status Theo's stop note set.
  completedStatusId: string;
  // Empty means an unassigned ticket gets no time entry.
  timeEntryResourceId: string;
  // Time Entry step: typed minutes per Theo L1 and L2, and for unclassified tickets. Empty inherits.
  timeEntryMinutes: TimeEntryDraft["timeEntryMinutes"];
  timeEntrySubMinutes: TimeEntryDraft["timeEntrySubMinutes"];
  timeEntryUncategorized: string;
}

interface TeamsDestinationOption {
  id: string;
  label: string;
}

function notifyLoadError(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  const text = String(message || "").trim();
  if (!text || text === "Network Error" || /^request failed with status/i.test(text)) {
    return "Unable to load Teams channels.";
  }
  return text;
}

function readTeamsDestinations(payload: unknown, key: "teams" | "channels"): TeamsDestinationOption[] {
  const body = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
  const rows = body[key];
  if (!Array.isArray(rows)) return [];
  const options: TeamsDestinationOption[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const item = row as { id?: string; label?: string };
    const id = String(item.id || "").trim();
    if (!id) continue;
    options.push({ id, label: String(item.label || id).trim() || id });
  }
  return options;
}

/** Tinyint, bit, and string flags all show up on this field. Only an explicit on value is enabled. */
function isEnabledFlag(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) && value !== 0;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    return text === "1" || text === "true" || text === "yes";
  }
  return false;
}

function draftFromAgent(agent: Agent): AgentDraft {
  const trigger: AgentTrigger | null = agent.trigger;
  const taxonomy = (trigger?.rules ?? []).flatMap((rule) => rule.theo_categories ?? []).filter(
    (selection): selection is { l1: string; l2: string | null } =>
      Boolean(selection && typeof selection.l1 === "string" && selection.l1.trim())
  );
  const hasFreeText =
    (trigger?.ticket_types ?? []).some((line) => line.trim().length > 0) ||
    (trigger?.rules ?? []).some((rule) => (rule.ticket_type_criteria ?? "").trim().length > 0);
  return {
    name: agent.name,
    description: agent.description ?? "",
    enabled: isEnabledFlag(agent.enabled),
    // A missing trigger is an unsaved agent: neither event fires. A saved trigger
    // that omits the list still means both, which is how older rows were stored.
    events: trigger ? normalizeAgentTriggerEvents(trigger.events) : [],
    updateFields: trigger ? normalizeAgentUpdateFields(trigger.update_fields) : [...AGENT_UPDATE_FIELDS],
    ticketTypes: listToLines(trigger?.ticket_types),
    queues: listToLines(trigger?.queues),
    statuses: listToLines(trigger?.statuses),
    priorities: listToLines(trigger?.priorities),
    assignees: listToLines(trigger?.assignees),
    customerIds: agent.all_customers ? [] : [...(agent.customer_ids ?? [])],
    ticketTypeMode: taxonomy.length > 0 || !hasFreeText ? "taxonomy" : "free",
    taxonomy: taxonomy.map((selection) => ({ l1: selection.l1, l2: selection.l2 ?? null })),
    instructions: agent.instructions ?? "",
    authorization: agentPolicyFromWire(agent.authorization),
    capacity: 3,
    interaction: agent.customer_interaction,
    // Nothing saved: a pause and a finish still notify the technician. Pickup does not,
    // and no Teams channel has been chosen.
    notifyEvents: agent.notify ? [...agent.notify.events] : ["needs_attention", "finished"],
    ...(agent.notify
      ? notifyTargetsFromNotify(agent.notify)
      : { notifyTechnician: true, notifyChannel: false }),
    notifyTeamId: agent.notify?.teams_team_id ?? "",
    notifyChannelId: agent.notify?.teams_channel_id ?? "",
    m365SensitiveWrites: normalizeM365SensitiveWriteModes(agent.autonomy?.m365_sensitive_writes),
    inProgressStatusId: agent.in_progress_status?.id ?? "",
    completedStatusId: agent.completed_status?.id ?? "",
    timeEntryResourceId: agent.time_entry_resource?.id ?? "",
    ...timeEntryDraftFromAgent(agent.time_entry_minutes),
  };
}

function emptyDraft(): AgentDraft {
  return {
    name: "",
    description: "",
    enabled: true,
    events: [...AUTOMATION_TRIGGER_EVENTS],
    updateFields: [...AGENT_UPDATE_FIELDS],
    ticketTypes: "",
    queues: "",
    statuses: "",
    priorities: "",
    assignees: "",
    customerIds: [],
    ticketTypeMode: "taxonomy",
    taxonomy: [],
    instructions: "",
    authorization: DEFAULT_AUTHORIZATION_POLICY,
    capacity: 3,
    interaction: 0,
    notifyEvents: [...AGENT_NOTIFY_EVENTS],
    notifyTechnician: true,
    notifyChannel: true,
    notifyTeamId: "",
    notifyChannelId: "",
    m365SensitiveWrites: defaultM365SensitiveWriteModes(),
    inProgressStatusId: "",
    completedStatusId: "",
    timeEntryResourceId: "",
    timeEntryMinutes: {},
    timeEntrySubMinutes: {},
    timeEntryUncategorized: "",
  };
}

/** The PSA's options after a first "unset" option, keeping a saved one the PSA no longer lists. */
function psaStatusSelectOptions(
  unsetLabel: string,
  statuses: AgentInProgressStatus[],
  selectedId: string,
  saved: AgentInProgressStatus | null | undefined
): { value: string; label: string }[] {
  const options = [{ value: "", label: unsetLabel }, ...statuses.map(status => ({ value: status.id, label: status.label }))];
  if (selectedId && !statuses.some(status => status.id === selectedId)) {
    options.push({ value: selectedId, label: saved?.id === selectedId ? saved.label : "Current status" });
  }
  return options;
}

interface AgentConfigureProps {
  slug?: string;
  creating?: boolean;
  /** Customer approvers and their directory. Injected in tests; Horizon otherwise. */
  approverStore?: AuthorizationSettingsStore;
  directory?: DirectorySource;
}

const AgentConfigure: React.FC<AgentConfigureProps> = ({
  slug,
  creating = false,
  approverStore = horizonAuthorizationSettingsStore,
  directory,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStep = stepFromTabParam(searchParams.get("tab"));
  const activeStep = requestedStep ?? 0;
  const setActiveStep = (step: number) => {
    const bounded = Math.min(STEP_TAB_PARAMS.length - 1, Math.max(0, Math.trunc(step)));
    const next = new URLSearchParams(searchParams);
    if (bounded === 0) next.delete("tab");
    else next.set("tab", STEP_TAB_PARAMS[bounded]);
    setSearchParams(next, { replace: true });
  };
  const { userMspInfo } = useUserMsp();
  const mspId = userMspInfo?.msp_id;
  const { agents, isLoading: agentsLoading, refresh } = useAgents(mspId);
  const { customers, getCustomers } = useIntegration();
  const approvers = useCustomerApprovers(approverStore);

  const agent = useMemo(
    () => (creating ? null : agents.find((row) => row.slug === slug) ?? null),
    [agents, creating, slug]
  );
  const [draft, setDraft] = useState<AgentDraft | null>(creating ? emptyDraft() : null);
  const [saving, setSaving] = useState(false);
  const [enabledSaving, setEnabledSaving] = useState(false);
  const enabledWriteLock = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);
  const { queueOptions, taxonomyOptions, timeEntryDefaults } = useAgentFilterOptions();
  const [taxonomyQuery, setTaxonomyQuery] = useState("");
  const [taxonomyGroup, setTaxonomyGroup] = useState<string | null>(null);
  const taxonomyGroups = useMemo(() => {
    const groups: Array<[string, AgentFilterTaxonomyOption[]]> = [];
    for (const option of taxonomyOptions) {
      const existing = groups.find((group) => group[0] === option.l1);
      if (existing) {
        existing[1].push(option);
      } else {
        groups.push([option.l1, [option]]);
      }
    }
    return groups;
  }, [taxonomyOptions]);
  // Theo L1 categories for the Time Entry step, in taxonomy order, each with its L2s; the defaults list the
  // L1s too if the taxonomy answer lacks them.
  const timeEntryCategories = useMemo(() => {
    const l1s = taxonomyOptions.filter((option) => option.l2 === null).map((option) => option.l1);
    const names = l1s.length > 0 ? l1s : Object.keys(timeEntryDefaults?.categories ?? {});
    return names.map((l1) => ({
      l1,
      l2s: taxonomyOptions.filter((option) => option.l1 === l1 && option.l2).map((option) => option.l2 as string),
    }));
  }, [taxonomyOptions, timeEntryDefaults]);
  useEffect(() => {
    const raw = searchParams.get("tab");
    if (raw == null || raw.trim() === "" || requestedStep !== null) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [requestedStep, searchParams, setSearchParams]);
  const [notifyTeams, setNotifyTeams] = useState<TeamsDestinationOption[]>([]);
  const [notifyChannels, setNotifyChannels] = useState<TeamsDestinationOption[]>([]);
  const [notifyTeamsLoaded, setNotifyTeamsLoaded] = useState(false);
  const [loadingNotifyTeams, setLoadingNotifyTeams] = useState(false);
  const [loadingNotifyChannels, setLoadingNotifyChannels] = useState(false);
  const [notifyChannelsTeamId, setNotifyChannelsTeamId] = useState("");
  const [notifyListError, setNotifyListError] = useState("");
  const [psaStatuses, setPsaStatuses] = useState<AgentInProgressStatus[]>([]);
  const [psaStatusesLoading, setPsaStatusesLoading] = useState(false);
  const [psaStatusesError, setPsaStatusesError] = useState("");
  // The PSA's users with a role they can log time under, for the time-entry fallback.
  const [psaUsers, setPsaUsers] = useState<AgentInProgressStatus[]>([]);
  const [psaUsersLoading, setPsaUsersLoading] = useState(false);
  const [psaUsersError, setPsaUsersError] = useState("");
  const [psaPriorities, setPsaPriorities] = useState<AgentInProgressStatus[]>([]);
  const [psaPrioritiesLoading, setPsaPrioritiesLoading] = useState(false);
  const [psaPrioritiesError, setPsaPrioritiesError] = useState("");

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    if (!creating && agent && !draft) {
      setDraft(draftFromAgent(agent));
    }
  }, [agent, creating, draft]);

  // Re-baseline after a successful save so the form settles to the stored row.
  // Only on save: edits must not reset while typing.
  useEffect(() => {
    if (savedTick > 0 && agent) {
      setDraft(draftFromAgent(agent));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedTick]);

  const notifyTeamId = draft?.notifyTeamId ?? "";
  const singleNotifyTeamId = notifyTeams.length === 1 ? notifyTeams[0].id : "";
  const channelTeamId = notifyTeamId || singleNotifyTeamId;
  const showNotifyTeamSelect =
    notifyTeams.length > 1 ||
    (notifyTeamId !== "" && notifyTeams.length > 0 && !notifyTeams.some((team) => team.id === notifyTeamId));

  useEffect(() => {
    if (activeStep !== COMMUNICATE_STEP) return;
    let cancelled = false;
    setLoadingNotifyTeams(true);
    setNotifyTeamsLoaded(false);
    setNotifyListError("");
    Promise.resolve(integrationApiInstance.getTeamsBotTeams())
      .then((payload) => {
        if (cancelled) return;
        setNotifyTeams(readTeamsDestinations(payload, "teams"));
      })
      .catch((error) => {
        if (cancelled) return;
        setNotifyTeams([]);
        setNotifyListError(notifyLoadError(error));
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingNotifyTeams(false);
          setNotifyTeamsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== COMMUNICATE_STEP) return;
    if (!channelTeamId) {
      setNotifyChannels([]);
      setNotifyChannelsTeamId("");
      setLoadingNotifyChannels(false);
      return;
    }
    let cancelled = false;
    setLoadingNotifyChannels(true);
    setNotifyChannelsTeamId("");
    Promise.resolve(integrationApiInstance.getTeamsBotChannels(channelTeamId))
      .then((payload) => {
        if (cancelled) return;
        setNotifyChannels(readTeamsDestinations(payload, "channels"));
        setNotifyChannelsTeamId(channelTeamId);
        setNotifyListError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setNotifyChannels([]);
        setNotifyChannelsTeamId(channelTeamId);
        setNotifyListError(notifyLoadError(error));
      })
      .finally(() => {
        if (!cancelled) setLoadingNotifyChannels(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep, channelTeamId]);

  useEffect(() => {
    if (activeStep !== AUTONOMY_STEP && activeStep !== 1) return;
    let cancelled = false;
    setPsaStatusesLoading(true);
    setPsaStatusesError("");
    Promise.resolve()
      .then(() => integrationApiInstance.getAutomationPsaStatuses())
      .then((payload) => {
        if (cancelled) return;
        const rows = Array.isArray(payload?.statuses) ? (payload.statuses as AgentInProgressStatus[]) : [];
        setPsaStatuses(rows.map((row) => ({ id: String(row.id), label: String(row.label) })));
      })
      .catch(() => {
        if (cancelled) return;
        setPsaStatuses([]);
        setPsaStatusesError("Could not load the PSA's statuses.");
      })
      .finally(() => {
        if (!cancelled) setPsaStatusesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== TIME_ENTRY_STEP && activeStep !== 1) return;
    let cancelled = false;
    setPsaUsersLoading(true);
    setPsaUsersError("");
    Promise.resolve()
      .then(() => integrationApiInstance.getAutomationPsaAssignees())
      .then((payload) => {
        if (cancelled) return;
        const rows = Array.isArray(payload?.assignees) ? (payload.assignees as AgentInProgressStatus[]) : [];
        setPsaUsers(rows.map((row) => ({ id: String(row.id), label: String(row.label) })));
      })
      .catch(() => {
        if (cancelled) return;
        setPsaUsers([]);
        setPsaUsersError("Could not load the PSA's users.");
      })
      .finally(() => {
        if (!cancelled) setPsaUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  useEffect(() => {
    if (activeStep !== 1) return;
    let cancelled = false;
    setPsaPrioritiesLoading(true);
    setPsaPrioritiesError("");
    Promise.resolve()
      .then(() => integrationApiInstance.getAutomationPsaPriorities())
      .then((payload) => {
        if (cancelled) return;
        const rows = Array.isArray(payload?.priorities) ? (payload.priorities as AgentInProgressStatus[]) : [];
        setPsaPriorities(rows.map((row) => ({ id: String(row.id), label: String(row.label) })));
      })
      .catch(() => {
        if (cancelled) return;
        setPsaPriorities([]);
        setPsaPrioritiesError("Could not load the PSA's priorities.");
      })
      .finally(() => {
        if (!cancelled) setPsaPrioritiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  const customerOptions = useMemo(() => {
    const rows = (Array.isArray((customers as { rows?: unknown })?.rows)
      ? (customers as { rows: Customer[] }).rows
      : Array.isArray(customers)
        ? (customers as Customer[])
        : []) as Customer[];
    return rows
      .filter((customer) => String(customer.msp_id) === String(mspId) && customer.name?.toLowerCase() !== "root")
      .map((customer) => ({ id: Number(customer.id), name: customer.name }))
      .filter((customer) => Number.isFinite(customer.id))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [customers, mspId]);

  if (!creating && !agentsLoading && !agent) {
    return <Navigate to="/theo-automations/monitor" replace />;
  }
  if (!draft || (!creating && !agent)) {
    return <Box sx={{ width: "100%", height: "100%", background: NEUTRAL.page }} />;
  }

  const baseline = creating ? emptyDraft() : draftFromAgent(agent!);
  const comparableDraft = (value: AgentDraft) => {
    const { enabled: _enabled, ...rest } = value;
    return { ...rest, events: [...rest.events].sort() };
  };
  const draftDirty = JSON.stringify(comparableDraft(draft)) !== JSON.stringify(comparableDraft(baseline));
  // Approvers save with the agent, so an approver change alone is enough to enable Save.
  const dirty = draftDirty || approvers.isDirty;
  const topicLines = linesToList(draft.ticketTypes);
  const queueLines = linesToList(draft.queues);
  const statusLines = linesToList(draft.statuses);
  const priorityLines = linesToList(draft.priorities);
  const assigneeLines = linesToList(draft.assignees);
  const queueChoices = queueFilterOptions(queueOptions, queueLines);
  const statusChoices = labeledFilterOptions(psaStatuses, statusLines);
  const priorityChoices = labeledFilterOptions(psaPriorities, priorityLines);
  const assigneeChoices = labeledFilterOptions(psaUsers, assigneeLines, [
    { id: UNASSIGNED_ASSIGNEE_ID, label: "Unassigned" },
  ]);
  // Free-text criteria a previous save stored on the trigger still count in free mode.
  const storedRuleCriteria = (agent?.trigger?.rules ?? []).some((rule) => (rule.ticket_type_criteria ?? "").trim());
  const hasTrigger =
    topicLines.length > 0 ||
    queueLines.length > 0 ||
    statusLines.length > 0 ||
    priorityLines.length > 0 ||
    assigneeLines.length > 0 ||
    (draft.ticketTypeMode === "taxonomy" ? draft.taxonomy.length > 0 : storedRuleCriteria);
  const trimmedName = draft.name.trim();
  const duplicateName =
    trimmedName.length > 0 &&
    agents.some((row) => row.id !== agent?.id && row.name.trim().toLowerCase() === trimmedName.toLowerCase());
  // Per-tab problems drive the red dots and gate saving. Both trigger events
  // may be off, which keeps the poller from starting this agent. Instructions
  // are optional. The name lives on step 1.
  const nameProblems = [
    ...(trimmedName ? [] : ["Give the agent a name."]),
    ...(duplicateName ? ["You already have an agent with that name."] : []),
  ];
  const listensForUpdates = draft.events.includes("ticket_updated");
  const stepProblems: string[][] = [
    nameProblems,
    [
      ...(draft.events.length > 0 && !hasTrigger
        ? ["Step 2 needs at least one queue, status, priority, assignee, ticket type, or Theo category."]
        : []),
      ...(listensForUpdates && draft.updateFields.length === 0
        ? ["Step 2 needs at least one field when ticket update is on."]
        : []),
    ],
    [],
    [],
    [],
    [
      ...(draft.notifyChannel && !draft.notifyChannelId
        ? ["Step 6 needs a Teams channel."]
        : []),
    ],
    timeEntryProblems(draft),
  ];
  const problems: string[] = stepProblems.flat();
  const canSave = problems.length === 0 && (creating || dirty) && !saving && !enabledSaving && !approvers.isSaving;

  // The header switch is not part of the stepped form. It saves on its own, so it does
  // not wait for Save changes and does not mark the rest of the form dirty.
  const handleEnabledChange = async (next: boolean) => {
    if (!draft || draft.enabled === next) return;
    if (creating || !agent) {
      setDraft({ ...draft, enabled: next });
      return;
    }
    if (enabledWriteLock.current) return;
    enabledWriteLock.current = true;
    const previous = draft.enabled;
    setDraft((current) => (current ? { ...current, enabled: next } : current));
    setEnabledSaving(true);
    setError(null);
    try {
      await integrationApiInstance.updateAgent(agent.id, { enabled: next });
      await refresh();
    } catch (toggleError) {
      setDraft((current) => (current ? { ...current, enabled: previous } : current));
      setError(toggleError instanceof Error ? toggleError.message : "Could not change whether this agent is enabled");
    } finally {
      enabledWriteLock.current = false;
      setEnabledSaving(false);
    }
  };

  const toggleEvent = (event: AutomationTriggerEvent) => {
    const current = new Set(draft.events);
    if (current.has(event)) {
      current.delete(event);
    } else {
      current.add(event);
    }
    const next = AUTOMATION_TRIGGER_EVENTS.filter((candidate) => current.has(candidate));
    setDraft({ ...draft, events: next });
  };

  const toggleTaxonomy = (l1: string, l2: string | null) => {
    const key = (selection: { l1: string; l2: string | null }) =>
      `${selection.l1}|${selection.l2 ?? ""}`;
    const target = `${l1}|${l2 ?? ""}`;
    setDraft({
      ...draft,
      taxonomy: draft.taxonomy.some((selection) => key(selection) === target)
        ? draft.taxonomy.filter((selection) => key(selection) !== target)
        : [...draft.taxonomy, { l1, l2: l2 ?? null }],
    });
  };

  // Taxonomy mode owns the single editable rule slot; free mode preserves any
  // previously stored free-text criteria and drops stale taxonomy selections.
  const rulesForSave = (): Array<{ ticket_type_criteria: string; theo_categories: Array<{ l1: string; l2: string | null }> }> => {
    if (draft.ticketTypeMode === "taxonomy") {
      return draft.taxonomy.length > 0 ? [{ ticket_type_criteria: "", theo_categories: draft.taxonomy }] : [];
    }
    return (agent?.trigger?.rules ?? [])
      .map((rule) => ({ ticket_type_criteria: rule.ticket_type_criteria ?? "", theo_categories: [] }))
      .filter((rule) => rule.ticket_type_criteria);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const rules = rulesForSave();
    const blankTrigger =
      draft.events.length === 0 &&
      topicLines.length === 0 &&
      queueLines.length === 0 &&
      statusLines.length === 0 &&
      priorityLines.length === 0 &&
      assigneeLines.length === 0 &&
      rules.length === 0;
    const payload: AgentInput = {
      name: trimmedName,
      description: draft.description.trim() || null,
      enabled: draft.enabled,
      instructions: draft.instructions.trim() || null,
      trigger:
        !creating && !agent?.trigger && blankTrigger
          ? null
          : {
              ticket_types: topicLines,
              queues: queueLines,
              statuses: statusLines,
              priorities: priorityLines,
              assignees: assigneeLines,
              rules,
              events: draft.events,
              update_fields: draft.updateFields,
            },
      automation_capacity: draft.capacity,
      customer_interaction: draft.interaction ?? 0,
      authorization: policyToWire(draft.authorization),
      notify: {
        technician_enabled: true,
        events: AGENT_NOTIFY_EVENTS.filter((event) => draft.notifyEvents.includes(event)),
        notify_technician: draft.notifyTechnician,
        notify_channel: draft.notifyChannel,
        teams_team_id: draft.notifyChannelId ? draft.notifyTeamId || channelTeamId : "",
        teams_channel_id: draft.notifyChannelId,
      },
      autonomy: {
        m365_sensitive_writes: draft.m365SensitiveWrites,
      },
      in_progress_status: draft.inProgressStatusId ? { id: draft.inProgressStatusId } : null,
      completed_status: draft.completedStatusId ? { id: draft.completedStatusId } : null,
      time_entry_resource: draft.timeEntryResourceId ? { id: draft.timeEntryResourceId } : null,
      time_entry_minutes: timeEntryMinutesForSave(draft),
      all_customers: draft.customerIds.length === 0,
      all_connected_apps: true,
      customer_ids: draft.customerIds,
      platform_ids: [],
      application_ids: [],
    };
    try {
      // Approvers are the customer's, not the agent's, so they save first on their own endpoints.
      const approversSaved = await approvers.save();
      if (!approversSaved.ok) {
        setError(approversSaved.message);
        return;
      }
      if (creating) {
        const created = await integrationApiInstance.createAgent(payload);
        await refresh();
        navigate(`/agents/${created.slug}/configure`);
        return;
      }
      await integrationApiInstance.updateAgent(agent!.id, payload);
      await refresh();
      setSavedTick((tick) => tick + 1);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : creating ? "Could not create the agent" : "Could not save the agent");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        background: NEUTRAL.page,
        px: { xs: 2, sm: 4 },
        py: 4,
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      <Box sx={{ maxWidth: 800, mx: "auto" }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: VIOLET.theoText,
          }}
        >
          AI Agent
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 0.75 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700, color: NEUTRAL.text, lineHeight: 1.2 }}>
              {creating ? "Create an Agent" : agent!.name}
            </Typography>
            <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: NEUTRAL.textSecondary, mt: 1 }}>
              Seven steps decide what this agent picks up, who can authorize its work, what it may do, who it
              talks to, and how much time it logs. The poller starts it when these filters match. A more specific agent wins when
              two match the same ticket.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={draft.enabled}
                disabled={enabledSaving || saving}
                onChange={(_event, checked) => {
                  void handleEnabledChange(checked);
                }}
                inputProps={{ "aria-label": "Enabled" }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: VIOLET.main },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: VIOLET.main },
                }}
              />
            }
            label={<Typography sx={{ fontSize: 13, fontWeight: 600, color: NEUTRAL.text }}>Enabled</Typography>}
            sx={{ flexShrink: 0, mr: 0 }}
          />
        </Box>

        <Tabs
          value={activeStep}
          onChange={(_event, value) => setActiveStep(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mt: 2.5,
            borderBottom: `1px solid ${NEUTRAL.border}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              color: NEUTRAL.textSecondary,
              minHeight: 44,
            },
            "& .MuiTab-root.Mui-selected": { color: VIOLET.theoText },
            "& .MuiTabs-indicator": { backgroundColor: VIOLET.main },
          }}
        >
          {STEP_TABS.map((label, tabIndex) => (
            <Tab
              key={label}
              aria-label={`Step ${tabIndex + 1}: ${label}`}
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  {`${tabIndex + 1}. ${label}`}
                  {stepProblems[tabIndex].length > 0 ? (
                    <Box
                      component="span"
                      sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#B3261E" }}
                    />
                  ) : null}
                </Box>
              }
            />
          ))}
        </Tabs>

        <Step
          index={1}
          hidden={activeStep !== 0}
          onNext={() => setActiveStep(1)}
          title="Name"
          help="Name this agent and say what it is for."
        >
          <Typography component="label" htmlFor="agent-name" sx={labelSx}>
            Agent name
          </Typography>
          <TextField
            id="agent-name"
            fullWidth
            size="small"
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            error={duplicateName}
            helperText={duplicateName ? "You already have an agent with that name." : " "}
            placeholder="Onboarding Specialist"
            sx={fieldSx}
          />
          <Typography component="label" htmlFor="agent-description" sx={{ ...labelSx, mt: 1.5 }}>
            Description
          </Typography>
          <TextField
            id="agent-description"
            fullWidth
            size="small"
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            placeholder="Handles new hire setup end to end."
            sx={{ ...fieldSx, mb: 0 }}
          />
        </Step>

        <Step
          index={2}
          hidden={activeStep !== 1}
          onNext={() => setActiveStep(2)}
          title="Filters and triggers: which tickets match, and when?"
          help="Queues, customers, status, priority, assignee, and ticket type decide which tickets match. Creation and update decide when this agent can pick one up. Leave both off and the poller will not start it, and no queue, status, priority, assignee, ticket type, or Theo category is required. Turn one on and at least one of those is required. Leave a list empty to include every value."
        >
          <Typography component="span" sx={labelSx}>
            When it fires
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.25, mb: 2 }}>
            {(AUTOMATION_TRIGGER_EVENTS as AutomationTriggerEvent[]).map((event) => {
              const checked = draft.events.includes(event);
              return (
                <Box
                  key={event}
                  onClick={() => toggleEvent(event)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: `${RADIUS.control}px`,
                    border: `1px solid ${checked ? VIOLET.main : NEUTRAL.border}`,
                    backgroundColor: checked ? VIOLET.wash : NEUTRAL.surface,
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onClick={(clickEvent) => clickEvent.stopPropagation()}
                    onChange={() => toggleEvent(event)}
                    sx={checkboxSx}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>
                      {TRIGGER_EVENT_COPY[event].title}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textSecondary }}>
                      {TRIGGER_EVENT_COPY[event].body}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {draft.events.includes("ticket_updated") ? (
            <Box sx={{ mb: 2 }}>
              <Typography component="span" sx={labelSx}>
                Update fields
              </Typography>
              <Typography sx={{ ...helpSx, mb: 1 }}>
                The poller runs this agent only when one of these fields changes.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {AGENT_UPDATE_FIELDS.map((field) => {
                  const selected = draft.updateFields.includes(field);
                  return (
                    <Chip
                      key={field}
                      label={UPDATE_FIELD_COPY[field]}
                      size="small"
                      onClick={() => {
                        const next = selected
                          ? draft.updateFields.filter((value) => value !== field)
                          : AGENT_UPDATE_FIELDS.filter((value) => value === field || draft.updateFields.includes(value));
                        setDraft({ ...draft, updateFields: next });
                      }}
                      sx={{
                        borderRadius: `${RADIUS.control}px`,
                        fontSize: 12.5,
                        backgroundColor: selected ? VIOLET.tint : NEUTRAL.subtle,
                        color: selected ? VIOLET.theoText : NEUTRAL.textSecondary,
                        border: `1px solid ${selected ? VIOLET.main : NEUTRAL.border}`,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ) : null}
          <Typography component="span" sx={labelSx}>
            a. PSA queues or categories
          </Typography>
          <Typography sx={{ ...helpSx, mb: 1 }}>
            Matched exactly — the most specific kind of filter. Leave unselected to include every queue.
          </Typography>
          {queueChoices.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textMuted }}>
              No queues or categories found for this MSP yet.
            </Typography>
          ) : (
            <LayeredMultiAutocompleteSelect
              label="Queues"
              inputAriaLabel="PSA queues or categories"
              size="small"
              options={queueChoices}
              selectedValues={queueLines}
              onChange={(ids) => setDraft({ ...draft, queues: ids.join("\n") })}
              emptySelectionMeansAll
              disableClearable={false}
              selectAllLabel="All queues"
              allSelectedLabel="All queues"
              pluralCountLabel="queues"
              noOptionsText="No queues or categories match that search."
              sx={fieldSx}
            />
          )}
          <Typography component="span" sx={{ ...labelSx, mt: 2 }}>
            b. Status
          </Typography>
          <Typography sx={{ ...helpSx, mb: 1 }}>
            Matched exactly. Leave unselected to include every status.
          </Typography>
          {statusChoices.length === 0 && !psaStatusesLoading ? (
            <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textMuted }}>
              {psaStatusesError || "No statuses found for this MSP yet."}
            </Typography>
          ) : (
            <LayeredMultiAutocompleteSelect
              label="Status"
              inputAriaLabel="Status"
              size="small"
              loading={psaStatusesLoading}
              options={statusChoices}
              selectedValues={statusLines}
              onChange={(ids) => setDraft({ ...draft, statuses: ids.join("\n") })}
              emptySelectionMeansAll
              disableClearable={false}
              selectAllLabel="All statuses"
              allSelectedLabel="All statuses"
              pluralCountLabel="statuses"
              noOptionsText="No statuses match that search."
              sx={fieldSx}
            />
          )}
          <Typography component="span" sx={{ ...labelSx, mt: 2 }}>
            c. Priority
          </Typography>
          <Typography sx={{ ...helpSx, mb: 1 }}>
            Matched exactly. Leave unselected to include every priority.
          </Typography>
          {priorityChoices.length === 0 && !psaPrioritiesLoading ? (
            <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textMuted }}>
              {psaPrioritiesError || "No priorities found for this MSP yet."}
            </Typography>
          ) : (
            <LayeredMultiAutocompleteSelect
              label="Priority"
              inputAriaLabel="Priority"
              size="small"
              loading={psaPrioritiesLoading}
              options={priorityChoices}
              selectedValues={priorityLines}
              onChange={(ids) => setDraft({ ...draft, priorities: ids.join("\n") })}
              emptySelectionMeansAll
              disableClearable={false}
              selectAllLabel="All priorities"
              allSelectedLabel="All priorities"
              pluralCountLabel="priorities"
              noOptionsText="No priorities match that search."
              sx={fieldSx}
            />
          )}
          <Typography component="span" sx={{ ...labelSx, mt: 2 }}>
            d. Assignee
          </Typography>
          <Typography sx={{ ...helpSx, mb: 1 }}>
            Matched exactly. Leave unselected to include every assignee, including unassigned tickets.
          </Typography>
          {psaUsersError ? (
            <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textMuted, mb: 1 }}>{psaUsersError}</Typography>
          ) : null}
          <LayeredMultiAutocompleteSelect
            label="Assignee"
            inputAriaLabel="Assignee"
            size="small"
            loading={psaUsersLoading}
            options={assigneeChoices}
            selectedValues={assigneeLines}
            onChange={(ids) => setDraft({ ...draft, assignees: ids.join("\n") })}
            emptySelectionMeansAll
            disableClearable={false}
            selectAllLabel="All assignees"
            allSelectedLabel="All assignees"
            pluralCountLabel="assignees"
            noOptionsText="No assignees match that search."
            sx={fieldSx}
          />
          <Typography component="span" sx={{ ...labelSx, mt: 2 }}>
            e. Customers
          </Typography>
          <Typography sx={{ ...helpSx, mb: 1 }}>
            Leave unselected to include every customer.
          </Typography>
          {customerOptions.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textMuted }}>
              No customers found for this MSP yet.
            </Typography>
          ) : (
            <LayeredMultiAutocompleteSelect
              label="Customers"
              inputAriaLabel="Customers"
              size="small"
              options={customerOptions.map((option) => ({ value: String(option.id), label: option.name }))}
              selectedValues={draft.customerIds.map(String)}
              onChange={(ids) =>
                setDraft({
                  ...draft,
                  customerIds: ids.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
                })
              }
              emptySelectionMeansAll
              disableClearable={false}
              selectAllLabel="All customers"
              allSelectedLabel="All customers"
              pluralCountLabel="customers"
              noOptionsText="No customers match that search."
              sx={fieldSx}
            />
          )}
          <Typography component="span" sx={{ ...labelSx, mt: 2 }}>
            f. Ticket type
          </Typography>
          <Select
            fullWidth
            size="small"
            value={draft.ticketTypeMode}
            onChange={(event) => setDraft({ ...draft, ticketTypeMode: event.target.value as "free" | "taxonomy" })}
            sx={{ ...selectSx, mb: 1 }}
          >
            <MenuItem value="taxonomy" sx={{ fontSize: 14 }}>
              Theo category
              <Box component="span" sx={{ color: NEUTRAL.textMuted, ml: 1, fontSize: 12.5 }}>
                Pick from the ticket taxonomy.
              </Box>
            </MenuItem>
            <MenuItem value="free" sx={{ fontSize: 14 }}>
              Free-text prompt
              <Box component="span" sx={{ color: NEUTRAL.textMuted, ml: 1, fontSize: 12.5 }}>
                Plain language, matched by meaning.
              </Box>
            </MenuItem>
          </Select>
          {draft.ticketTypeMode === "free" ? (
            <>
              <Typography sx={{ ...helpSx, mb: 1 }}>
                Plain language, matched by meaning rather than exact words. One per line.
              </Typography>
              <TextField
                id="agent-topics"
                fullWidth
                multiline
                minRows={3}
                value={draft.ticketTypes}
                onChange={(event) => setDraft({ ...draft, ticketTypes: event.target.value })}
                placeholder={"new hire onboarding\nM365 account creation"}
                sx={fieldSx}
              />
            </>
          ) : (
            <>
              <Typography sx={{ ...helpSx, mb: 1 }}>
                Matched from the ticket classification. Search, or open one group and pick inside it.
              </Typography>
              {draft.taxonomy.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
                  {draft.taxonomy.map((selection) => {
                    const label = selection.l2 ? `${selection.l1} > ${selection.l2}` : `All of ${selection.l1}`;
                    return (
                      <Chip
                        key={`${selection.l1}|${selection.l2 ?? ""}`}
                        label={label}
                        size="small"
                        onDelete={() => toggleTaxonomy(selection.l1, selection.l2)}
                        sx={{
                          borderRadius: `${RADIUS.control}px`,
                          fontSize: 12.5,
                          backgroundColor: VIOLET.tint,
                          color: VIOLET.theoText,
                          border: `1px solid ${VIOLET.main}`,
                        }}
                      />
                    );
                  })}
                </Box>
              ) : null}
              {taxonomyOptions.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textMuted }}>
                  No Theo categories available.
                </Typography>
              ) : (
                <TaxonomyBrowser
                  groups={taxonomyGroups}
                  query={taxonomyQuery}
                  onQuery={setTaxonomyQuery}
                  group={taxonomyGroup}
                  onGroup={setTaxonomyGroup}
                  selected={draft.taxonomy}
                  onToggle={toggleTaxonomy}
                />
              )}
            </>
          )}
        </Step>

        <Step
          index={3}
          hidden={activeStep !== 2}
          onNext={() => setActiveStep(AUTHORIZATIONS_STEP)}
          title="Instructions"
          help="Operating notes added to this agent's turns on top of its automation level. Instructions can narrow how it works; they cannot widen what it is allowed to do."
        >
          <TextField
            id="agent-instructions"
            fullWidth
            multiline
            minRows={4}
            disabled
            value={draft.instructions}
            placeholder="Always confirm the manager before granting group access."
            sx={{
              ...fieldSx,
              "& .MuiOutlinedInput-root.Mui-disabled": {
                backgroundColor: NEUTRAL.subtle,
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: NEUTRAL.textMuted,
              },
            }}
          />
        </Step>

        <Step
          index={4}
          hidden={activeStep !== AUTHORIZATIONS_STEP}
          onNext={() => setActiveStep(4)}
          title="Authorizations: who can authorize this agent's work?"
          help="Before this agent starts a ticket in Auto Resolver, it checks that the right person asked for or approved the work. This policy is this agent's own."
        >
          <AgentAuthorizationsStep
            policy={draft.authorization}
            onPolicyChange={(authorization) => setDraft({ ...draft, authorization })}
            approvers={approvers}
            customerOptions={customerOptions.map((customer) => ({ label: customer.name, value: String(customer.id) }))}
            customersLoading={false}
            directory={directory}
          />
        </Step>

        <Step
          index={5}
          hidden={activeStep !== AUTONOMY_STEP}
          onNext={() => setActiveStep(COMMUNICATE_STEP)}
          title="Autonomy: how much should this agent do?"
          help="This agent runs as Auto Resolver. Sensitive Microsoft 365 writes can still wait for a technician."
        >
          <AutoResolverSummary />
          <M365SensitiveWritesControl
            value={draft.m365SensitiveWrites}
            onChange={(m365SensitiveWrites) => setDraft({ ...draft, m365SensitiveWrites })}
          />
          <Box component="section" aria-labelledby="agent-in-progress-status" sx={{ ...notifySectionSx, mt: 2 }}>
            <Typography
              id="agent-in-progress-status"
              component="h3"
              sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}
            >
              Status when Theo starts
            </Typography>
            <Typography sx={{ ...helpSx, mt: 0.5, mb: 1.5 }}>
              The PSA status a ticket moves to once this agent decides it can work it.
            </Typography>
            <LayeredAutocompleteSelect
              label=""
              id="agent-in-progress-status-select"
              fullWidth
              size="small"
              disabled={psaStatusesLoading}
              value={draft.inProgressStatusId}
              onChange={value => setDraft({ ...draft, inProgressStatusId: String(value) })}
              sx={selectSx}
              inputAriaLabel="Status when Theo starts"
              disableClearable
              options={psaStatusSelectOptions(`The PSA's "In Progress" status`, psaStatuses, draft.inProgressStatusId, agent?.in_progress_status)}
            />
            {psaStatusesError ? (
              <Typography role="alert" sx={{ ...helpSx, mt: 1, color: "error.main" }}>
                {psaStatusesError}
              </Typography>
            ) : null}
          </Box>
          <Box component="section" aria-labelledby="agent-completed-status" sx={{ ...notifySectionSx, mt: 2 }}>
            <Typography
              id="agent-completed-status"
              component="h3"
              sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}
            >
              Status when Theo finishes
            </Typography>
            <Typography sx={{ ...helpSx, mt: 0.5, mb: 1.5 }}>
              The PSA status a ticket moves to once this agent completes it. Theo also logs a time entry then.
            </Typography>
            <LayeredAutocompleteSelect
              label=""
              id="agent-completed-status-select"
              fullWidth
              size="small"
              disabled={psaStatusesLoading}
              value={draft.completedStatusId}
              onChange={value => setDraft({ ...draft, completedStatusId: String(value) })}
              sx={selectSx}
              inputAriaLabel="Status when Theo finishes"
              disableClearable
              options={psaStatusSelectOptions("Leave the status unchanged", psaStatuses, draft.completedStatusId, agent?.completed_status)}
            />
          </Box>
        </Step>

        <Step
          index={6}
          hidden={activeStep !== COMMUNICATE_STEP}
          onNext={() => setActiveStep(TIME_ENTRY_STEP)}
          title="Communicate"
          help="Which notifications this agent sends, and where they go."
        >
          <Box component="section" aria-labelledby="agent-notify-which" sx={notifySectionSx}>
          <Typography id="agent-notify-which" component="h3" sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>
            Which notifications
          </Typography>
          <Typography sx={{ ...helpSx, mt: 0.5, mb: 1.5 }}>
            The moments that send a message.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {AGENT_NOTIFY_EVENTS.map((event) => {
              const checked = draft.notifyEvents.includes(event);
              const nextEvents = checked
                ? draft.notifyEvents.filter((value) => value !== event)
                : AGENT_NOTIFY_EVENTS.filter((value) => value === event || draft.notifyEvents.includes(value));
              return (
                <Box
                  key={event}
                  onClick={() => setDraft({ ...draft, notifyEvents: nextEvents })}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    px: 1.5,
                    py: 1,
                    borderRadius: `${RADIUS.control}px`,
                    border: `1px solid ${checked ? VIOLET.main : NEUTRAL.border}`,
                    backgroundColor: checked ? VIOLET.wash : NEUTRAL.surface,
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => setDraft({ ...draft, notifyEvents: nextEvents })}
                    sx={checkboxSx}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>
                      {NOTIFY_EVENT_COPY[event].title}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textSecondary }}>
                      {NOTIFY_EVENT_COPY[event].body}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          </Box>
          <Box component="section" aria-labelledby="agent-notify-where" sx={{ ...notifySectionSx, mt: 2 }}>
          <Typography id="agent-notify-where" component="h3" sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>
            Where notifications go
          </Typography>
          <Typography sx={{ ...helpSx, mt: 0.5, mb: 1.5 }}>
            The technician, a Teams channel, or both. Both selected sends each message to each of them. If the technician is not available, the message goes to the Teams channel.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {(
              [
                ["notifyTechnician", "Technician", "Direct message to the technician assigned on the ticket. If they are not available, the message goes to the Teams channel."],
                ["notifyChannel", "Teams channel", "Post in the Teams channel, including when the technician is not available."],
              ] as const
            ).map(([field, title, body]) => {
              const checked = draft[field];
              return (
                <Box
                  key={field}
                  onClick={() => setDraft({ ...draft, [field]: !checked })}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    px: 1.5,
                    py: 1,
                    borderRadius: `${RADIUS.control}px`,
                    border: `1px solid ${checked ? VIOLET.main : NEUTRAL.border}`,
                    backgroundColor: checked ? VIOLET.wash : NEUTRAL.surface,
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    inputProps={{ "aria-label": title }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => setDraft({ ...draft, [field]: !checked })}
                    sx={checkboxSx}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>{title}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: NEUTRAL.textSecondary }}>{body}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {draft.notifyChannel && showNotifyTeamSelect ? (
            <>
              <Typography id="agent-notify-team-label" component="label" sx={{ ...labelSx, mt: 2 }}>
                Teams team
              </Typography>
              <LayeredAutocompleteSelect
                label=""
                id="agent-notify-team"
                fullWidth
                size="small"
                value={draft.notifyTeamId}
                onChange={value => setDraft({ ...draft, notifyTeamId: String(value), notifyChannelId: "" })}
                sx={selectSx}
                inputAriaLabel="Teams team"
                placeholder="Select a team"
                disableClearable
                options={[{ value: "", label: "Select a team" }, ...notifyTeams.map(team => ({ value: team.id, label: team.label })), ...(draft.notifyTeamId && !notifyTeams.some(team => team.id === draft.notifyTeamId) ? [{ value: draft.notifyTeamId, label: "Current team" }] : [])]}
              />
            </>
          ) : null}
          {draft.notifyChannel ? (
            <>
          <Typography id="agent-notify-channel-label" component="label" sx={{ ...labelSx, mt: 2 }}>
            Channel
          </Typography>
          <LayeredAutocompleteSelect
            label=""
            id="agent-notify-channel"
            fullWidth
            size="small"
            disabled={
              loadingNotifyTeams ||
              loadingNotifyChannels ||
              (Boolean(channelTeamId) && notifyChannelsTeamId !== channelTeamId) ||
              (showNotifyTeamSelect && !draft.notifyTeamId)
            }
            value={draft.notifyChannelId}
            onChange={value => {
              const nextChannel = String(value);
              setDraft({
                ...draft,
                notifyChannelId: nextChannel,
                notifyTeamId: nextChannel
                  ? draft.notifyTeamId || channelTeamId
                  : showNotifyTeamSelect
                    ? draft.notifyTeamId
                    : "",
              });
            }}
            sx={selectSx}
            inputAriaLabel="Channel"
            placeholder="Select a channel"
            disableClearable
            options={[...notifyChannels.map(channel => ({ value: channel.id, label: channel.label })), ...(draft.notifyChannelId && !notifyChannels.some(channel => channel.id === draft.notifyChannelId) ? [{ value: draft.notifyChannelId, label: "Current Teams channel" }] : [])]}
          />
          <Typography sx={{ ...helpSx, mt: 0.75 }}>
            {notifyListError
              ? notifyListError
              : loadingNotifyTeams || loadingNotifyChannels || (Boolean(channelTeamId) && notifyChannelsTeamId !== channelTeamId)
                ? "Loading Teams channels…"
                : showNotifyTeamSelect && !draft.notifyTeamId
                  ? "Select a team to see its channels."
                  : notifyTeamsLoaded && notifyTeams.length === 0
                    ? "No Teams channels found."
                    : channelTeamId && notifyChannelsTeamId === channelTeamId && notifyChannels.length === 0
                      ? "No channels found in this team."
                      : "Pick the Teams channel for this agent."}
          </Typography>
            </>
          ) : null}
          </Box>
        </Step>

        <Step
          index={7}
          hidden={activeStep !== TIME_ENTRY_STEP}
          title="Time Entry: how much time should Theo log?"
          help={`When this agent completes a ticket on its own, Theo logs a time entry on it. The minutes depend on the ticket's Theo category; a ticket in more than one logs the highest of their minutes. Open a category to set its subcategories; one left empty follows its category. Leave a field empty to use the value shown in it.`}
        >
          <Box component="section" aria-labelledby="agent-time-entry-minutes" sx={notifySectionSx}>
            <Typography
              id="agent-time-entry-minutes"
              component="h3"
              sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}
            >
              Minutes per ticket category
            </Typography>
            <Typography sx={{ ...helpSx, mt: 0.5, mb: 1.5 }}>
              Whole minutes from {TIME_ENTRY_MIN_MINUTES} to {TIME_ENTRY_MAX_MINUTES}. Only the categories you change are saved,
              so the rest keep following Theo&apos;s defaults.
            </Typography>
            {timeEntryCategories.length === 0 ? (
              <Typography role="status" sx={helpSx}>
                Loading ticket categories…
              </Typography>
            ) : (
              <TimeEntryMinutesList
                categories={timeEntryCategories}
                defaults={timeEntryDefaults}
                draft={draft}
                onChange={(patch) => setDraft({ ...draft, ...patch })}
              />
            )}
          </Box>
          <Box component="section" aria-labelledby="agent-time-entry-resource" sx={{ ...notifySectionSx, mt: 2 }}>
            <Typography
              id="agent-time-entry-resource"
              component="h3"
              sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}
            >
              Log time as default
            </Typography>
            <Typography sx={{ ...helpSx, mt: 0.5, mb: 1.5 }}>
              Theo logs its time under the ticket&apos;s assigned technician. When the ticket has none, it uses this user.
            </Typography>
            <LayeredAutocompleteSelect
              label=""
              id="agent-time-entry-resource-select"
              fullWidth
              size="small"
              disabled={psaUsersLoading}
              value={draft.timeEntryResourceId}
              onChange={value => setDraft({ ...draft, timeEntryResourceId: String(value) })}
              sx={selectSx}
              inputAriaLabel="Log time as default"
              disableClearable
              options={psaStatusSelectOptions("No time entry on unassigned tickets", psaUsers, draft.timeEntryResourceId, agent?.time_entry_resource)}
            />
            {psaUsersError ? (
              <Typography role="alert" sx={{ ...helpSx, mt: 1, color: "error.main" }}>
                {psaUsersError}
              </Typography>
            ) : null}
          </Box>
        </Step>

        {problems.length > 0 && dirty ? (
          <Box sx={{ mt: 2 }}>
            {problems.map((problem) => (
              <Typography key={problem} sx={{ fontSize: 12.5, color: VIOLET.theoText, mt: 0.5 }}>
                {problem}
              </Typography>
            ))}
          </Box>
        ) : null}
        {error ? (
          <Typography
            role="alert"
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              fontSize: 13,
              borderRadius: `${RADIUS.control}px`,
              color: NEUTRAL.text,
              backgroundColor: VIOLET.wash,
              border: `1px solid ${VIOLET.tint}`,
            }}
          >
            {error}
          </Typography>
        ) : null}

        <Box sx={{ mt: 3, display: "flex", gap: 1.25, alignItems: "center" }}>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            disableElevation
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: `${RADIUS.control}px`,
              backgroundColor: VIOLET.main,
              "&:hover": { backgroundColor: VIOLET.dark },
            }}
          >
            {saving ? (creating ? "Creating…" : "Saving…") : creating ? "Create agent" : "Save changes"}
          </Button>
          <Button
            onClick={() => {
              if (creating) {
                navigate("/theo-automations/monitor");
                return;
              }
              approvers.discard();
              if (agent) setDraft(draftFromAgent(agent));
            }}
            disabled={creating ? saving : !dirty || saving || enabledSaving}
            sx={{ textTransform: "none", fontSize: 14, color: NEUTRAL.textSecondary, borderRadius: `${RADIUS.control}px` }}
          >
            {creating ? "Cancel" : "Discard"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const AgentConfigureRoute: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  if (!agentId) {
    return <Navigate to="/theo-automations/monitor" replace />;
  }
  return <AgentConfigure slug={agentId} />;
};

export { AgentConfigure };
export default AgentConfigureRoute;
